/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useEffect } from 'react';
import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { useKeycloak } from '@react-keycloak/web';
import { ApiError } from '@app/api/core/ApiError';
import { ensureCorrelationIdHeader } from '@app/api/core/correlationId';
import { REQUEST_TIMEOUT_MS } from '@app/api/core/httpTimeout';
import { tryParseApiEnvelopeFromBlob } from '@app/api/core/tryParseApiEnvelopeFromBlob';
import { getEnvelopeMessage } from '@app/api/core/getEnvelopeMessage';

export const useCreateHttpApi = (): AxiosInstance => {
  const { keycloak } = useKeycloak();
  const token = keycloak.token;
  const subscriptionKey = process.env.REACT_APP_OCP_APIM_SUBSCRIPTION_KEY;

  const httpRef = useRef<AxiosInstance | null>(null);
  if (!httpRef.current) {
    httpRef.current = axios.create({
      baseURL: process.env.REACT_APP_BASE_URL_CORE,
      timeout: REQUEST_TIMEOUT_MS,
      headers: subscriptionKey
        ? {
            'Ocp-Apim-Subscription-Key': subscriptionKey,
          }
        : undefined,
    });

    httpRef.current.interceptors.request.use((config: AxiosRequestConfig) => {
      config.headers = ensureCorrelationIdHeader(config.headers);
      return config;
    });

    httpRef.current.interceptors.response.use(
      (res) => res,
      async (error: AxiosError) => {
        const status = error.response?.status;
        let data = error.response?.data;

        if (data instanceof Blob) {
          const envelope = await tryParseApiEnvelopeFromBlob(data);
          if (envelope) {
            data = envelope;
          }
        }

        const msg =
          getEnvelopeMessage(data as any) ||
          (data as any)?.error?.message ||
          (data as any)?.message ||
          error.message;
        throw new ApiError<ApiErrorData>(msg, data, status);
      }
    );
  }

  useEffect(() => {
    if (httpRef.current) {
      httpRef.current.defaults.headers.common.Authorization = token
        ? `Bearer ${token}`
        : '';
    }
  }, [token]);

  return httpRef.current!;
};

export const useCreateWithCustomHeaders = (
  customHeaders: Record<string, string>
): AxiosInstance => {
  const http = useCreateHttpApi();

  useEffect(() => {
    const id = http.interceptors.request.use((config: AxiosRequestConfig) => {
      config.headers = {
        ...config.headers,
        ...customHeaders,
      };
      return config;
    });

    return () => {
      http.interceptors.request.eject(id);
    };
  }, [http, customHeaders]);

  return http;
};

export const useHttpApi = (): AxiosInstance => useCreateHttpApi();
export const useHttpApiWithCustomHeaders = (
  customHeaders: Record<string, string>
): AxiosInstance => useCreateWithCustomHeaders(customHeaders);

export interface ApiErrorData {
  message: string;
}
